import {
	AlertCircle,
	ArrowLeft,
	CheckCircle2,
	Eye,
	EyeOff,
	Home,
	Loader2,
	ShieldCheck,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useForceChangePassword } from '@/api/queries/auth-queries'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ROUTES } from '@/constants/routes'
import { useAuthStore } from '@/store'

const MIN_PASSWORD_LENGTH = 6

function getPasswordStrength(password: string) {
	let score = 0
	if (password.length >= MIN_PASSWORD_LENGTH) score++
	if (password.length >= 8) score++
	if (/[A-Z]/.test(password)) score++
	if (/[0-9]/.test(password)) score++
	if (/[^A-Za-z0-9]/.test(password)) score++
	return score
}

function getStrengthLabel(score: number) {
	if (score <= 1) return { label: 'Fraca', color: 'bg-red-500' }
	if (score <= 2) return { label: 'Razoável', color: 'bg-orange-500' }
	if (score <= 3) return { label: 'Boa', color: 'bg-yellow-500' }
	return { label: 'Forte', color: 'bg-green-500' }
}

export const ForceChangePasswordPage = () => {
	const navigate = useNavigate()
	const { user, logout, updateUser } = useAuthStore()
	const { mutateAsync, isPending } = useForceChangePassword()

	const submitted = useRef(false)

	const [currentPassword, setCurrentPassword] = useState('')
	const [newPassword, setNewPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [showCurrentPassword, setShowCurrentPassword] = useState(false)
	const [showNewPassword, setShowNewPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)
	const [isError, setIsError] = useState(false)
	const [error, setError] = useState('')
	const [isPasswordChanged, setIsPasswordChanged] = useState(false)

	// Guard: only accessible if user needs to change password
	useEffect(() => {
		if (!submitted.current && !user?.shouldChangePassword) {
			navigate(ROUTES.PUBLIC.LOGIN, { replace: true })
		}
	}, [user, navigate])

	const strength = getPasswordStrength(newPassword)
	const { label: strengthLabel, color: strengthColor } =
		getStrengthLabel(strength)
	const passwordsMatch = newPassword === confirmPassword
	const isValid =
		currentPassword.length > 0 &&
		newPassword.length >= MIN_PASSWORD_LENGTH &&
		passwordsMatch

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setIsError(false)

		if (!passwordsMatch) {
			setIsError(true)
			setError('As senhas não coincidem.')
			return
		}

		if (newPassword.length < MIN_PASSWORD_LENGTH) {
			setIsError(true)
			setError(`A senha deve ter no mínimo ${MIN_PASSWORD_LENGTH} caracteres.`)
			return
		}

		try {
			await mutateAsync({ newPassword })

			submitted.current = true

			const currentUser = user

			if (!currentUser) {
				setIsError(true)
				setError('Usuário não encontrado.')
				return
			}

			updateUser({
				...currentUser,
				shouldChangePassword: false,
			})

			toast.success('Senha definida com sucesso!')
			setIsPasswordChanged(true)
		} catch (_err) {
			setIsError(true)
			setError('Erro ao alterar a senha. Tente novamente.')
		}
	}

	if (!submitted.current && !user?.shouldChangePassword) {
		return null
	}

	if (!user) {
		return null
	}

	const renderHeader = () => {
		return (
			<div
				className={`px-6 pb-8 pt-10 text-center sm:rounded-t-2xl sm:px-8 sm:pb-6 sm:pt-8 ${
					isPasswordChanged ? 'bg-kaefer-grass-green' : 'bg-kaefer-dark-grey'
				}`}
			>
				<div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white/15">
					{isPasswordChanged ? (
						<CheckCircle2 className="h-7 w-7 text-white" />
					) : (
						<ShieldCheck className="h-7 w-7 text-white" />
					)}
				</div>
				<h1 className="text-2xl font-semibold text-white">
					{isPasswordChanged ? 'Sucesso!' : 'Definir nova senha'}
				</h1>
				{!isPasswordChanged && (
					<p className="mt-1.5 text-sm text-white/75">
						Olá, <span className="font-medium">{user.name ?? 'Usuário'}</span>.
						<br />
						Por segurança, defina uma nova senha para continuar.
					</p>
				)}
			</div>
		)
	}

	const changeSuccess = () => {
		return (
			<div className="flex flex-col items-center justify-center space-y-6 flex-1">
				<p className="text-kaefer-dark-grey text-base leading-relaxed text-center">
					Sua conta está agora segura e você pode começar a usar todas as
					funcionalidades da plataforma.
				</p>
			</div>
		)
	}

	const changePasswordForm = () => {
		return (
			<form className="space-y-5" onSubmit={onSubmit}>
				{/* Senha atual */}
				<div className="space-y-2">
					<Label
						className="font-medium text-kaefer-dark-grey text-sm"
						htmlFor="currentPassword"
					>
						Senha atual
					</Label>
					<div className="flex h-11 items-center rounded-md border border-kaefer-silver px-3 focus-within:border-kaefer-dark-grey">
						<Input
							className="flex-1 border-0 p-0 shadow-none outline-none focus-visible:ring-0"
							disabled={isPending}
							id="currentPassword"
							onChange={(e) => setCurrentPassword(e.target.value)}
							placeholder="Senha atual (padrão legado)"
							required
							autoFocus
							type={showCurrentPassword ? 'text' : 'password'}
							value={currentPassword}
						/>
						<Button
							variant="password-icon"
							size="icon-sm"
							disabled={isPending}
							onClick={() => setShowCurrentPassword(!showCurrentPassword)}
							type="button"
						>
							{showCurrentPassword ? (
								<EyeOff className="h-4 w-4" />
							) : (
								<Eye className="h-4 w-4" />
							)}
						</Button>
					</div>
				</div>

				{/* Nova senha */}
				<div className="space-y-2">
					<Label
						className="font-medium text-kaefer-dark-grey text-sm"
						htmlFor="newPassword"
					>
						Nova senha
					</Label>
					<div
						className={`flex h-11 items-center rounded-md border px-3 ${
							confirmPassword.length > 0 && !passwordsMatch
								? 'border-red-500'
								: 'border-kaefer-silver focus-within:border-kaefer-dark-grey'
						}`}
					>
						<Input
							className="flex-1 border-0 p-0 shadow-none outline-none focus-visible:ring-0"
							disabled={isPending}
							id="newPassword"
							onChange={(e) => setNewPassword(e.target.value)}
							placeholder="Mínimo 6 caracteres"
							required
							type={showNewPassword ? 'text' : 'password'}
							value={newPassword}
						/>
						<Button
							variant="password-icon"
							size="icon-sm"
							disabled={isPending}
							onClick={() => setShowNewPassword(!showNewPassword)}
							type="button"
						>
							{showNewPassword ? (
								<EyeOff className="h-4 w-4" />
							) : (
								<Eye className="h-4 w-4" />
							)}
						</Button>
					</div>

					{/* Força da senha */}
					{newPassword.length > 0 && (
						<div className="space-y-1">
							<div className="flex gap-1">
								{Array.from({ length: 5 }).map((_, i) => (
									<div
										className={`h-1.5 flex-1 rounded-full ${i < strength ? strengthColor : 'bg-gray-200'}`}
										key={`strength-${i.toString()}`}
									/>
								))}
							</div>
							<p className="text-kaefer-medium-grey text-xs">
								Força: {strengthLabel}
							</p>
						</div>
					)}
				</div>

				{/* Confirmar nova senha */}
				<div className="space-y-2">
					<Label
						className="font-medium text-kaefer-dark-grey text-sm"
						htmlFor="confirmPassword"
					>
						Confirmar nova senha
					</Label>
					<div
						className={`flex h-11 items-center rounded-md border px-3 ${
							confirmPassword.length > 0 && !passwordsMatch
								? 'border-red-500'
								: 'border-kaefer-silver focus-within:border-kaefer-dark-grey'
						}`}
					>
						<Input
							className="flex-1 border-0 p-0 shadow-none outline-none focus-visible:ring-0"
							disabled={isPending}
							id="confirmPassword"
							onChange={(e) => setConfirmPassword(e.target.value)}
							placeholder="Repita a nova senha"
							required
							type={showConfirmPassword ? 'text' : 'password'}
							value={confirmPassword}
						/>
						<Button
							variant="password-icon"
							size="icon-sm"
							disabled={isPending}
							onClick={() => setShowConfirmPassword(!showConfirmPassword)}
							type="button"
						>
							{showConfirmPassword ? (
								<EyeOff className="h-4 w-4" />
							) : (
								<Eye className="h-4 w-4" />
							)}
						</Button>
					</div>
					{confirmPassword.length > 0 && !passwordsMatch && (
						<p className="text-red-500 text-xs">As senhas não coincidem.</p>
					)}
				</div>

				{/* Error */}
				{isError && (
					<Alert variant="destructive">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}

				{/* Submit */}
				<Button
					disabled={isPending || !isValid}
					type="submit"
					variant="kaefer-grey"
				>
					{isPending ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							Salvando...
						</>
					) : (
						'Definir nova senha'
					)}
				</Button>
			</form>
		)
	}

	return (
		<div className="min-h-dvh bg-white sm:flex sm:items-center sm:justify-center sm:bg-kaefer-light-silver sm:p-6">
			<div className="flex min-h-dvh flex-col sm:min-h-0 sm:w-full sm:max-w-md sm:rounded-2xl sm:bg-white sm:shadow-xl">
				{/* Header */}
				{renderHeader()}
				{/* Content */}
				<div className="flex-1 flex-col flex px-6 py-8 sm:px-8 sm:pb-8 sm:pt-6 justify-between">
					{isPasswordChanged ? changeSuccess() : changePasswordForm()}

					{/* Action Buttons */}
					<div>
						{!isPasswordChanged && (
							<Button
								onClick={() => {
									logout()
									navigate(ROUTES.PUBLIC.LOGIN, { replace: true })
								}}
								variant="outline"
								className="mt-5 w-full"
							>
								<ArrowLeft className="h-3.5 w-3.5" />
								Voltar ao login
							</Button>
						)}

						{isPasswordChanged && (
							<Button
								className="mt-6 w-full"
								variant="kaefer-grey"
								onClick={() =>
									navigate(ROUTES.DASHBOARD.HOME, { replace: true })
								}
							>
								<Home className="mr-2 h-4 w-4" />
								Ir para home
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
